import React, { useEffect, useId, useState } from 'react';
import Modal from '../Modal';
import { useTranslation } from '../../i18n';
import './DurationCorrectionModal.css';

const MAX_HOURS = 24;

interface DurationCorrectionModalProps {
  isOpen: boolean;
  todoTitle?: string;
  recordedSeconds: number;
  /** Shown when the modal opens right after stopping a forgotten timer. */
  isLongRunningPrompt?: boolean;
  onSave: (durationSeconds: number) => Promise<void>;
  onClose: () => void;
}

const DurationCorrectionModal: React.FC<DurationCorrectionModalProps> = ({
  isOpen,
  todoTitle,
  recordedSeconds,
  isLongRunningPrompt = false,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const formatHm = (seconds: number) =>
    t('timer.hoursMinutes', { hours: Math.floor(seconds / 3600), minutes: Math.floor((seconds % 3600) / 60) });
  const hoursId = useId();
  const minutesId = useId();
  const [hours, setHours] = useState('0');
  const [minutes, setMinutes] = useState('0');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const capped = Math.min(recordedSeconds, MAX_HOURS * 3600);
    setHours(String(Math.floor(capped / 3600)));
    setMinutes(String(Math.floor((capped % 3600) / 60)));
    setIsSaving(false);
  }, [isOpen, recordedSeconds]);

  const h = Number(hours);
  const m = Number(minutes);
  const total = h * 3600 + m * 60;
  const isValid = Number.isInteger(h) && Number.isInteger(m)
    && h >= 0 && m >= 0 && m < 60 && total <= MAX_HOURS * 3600;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(total);
    } finally {
      setIsSaving(false);
    }
  };

  const footer = (
    <div className="duration-modal__actions">
      <button type="button" className="duration-modal__btn is-secondary" onClick={onClose} disabled={isSaving}>
        {isLongRunningPrompt ? t('timer.keepRecorded') : t('common.cancel')}
      </button>
      <button type="submit" form="duration-correction-form" className="duration-modal__btn is-primary" disabled={!isValid || isSaving}>
        {isSaving ? t('common.saving') : t('timer.saveDuration')}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isLongRunningPrompt ? t('timer.longRunningTitle') : t('timer.correctDuration')}
      footer={footer}
      size="sm"
      disableClose={isSaving}
      contentClassName="duration-modal"
    >
      <form id="duration-correction-form" onSubmit={handleSubmit}>
        <p className="duration-modal__lead">
          {isLongRunningPrompt
            ? t('timer.longRunningMessage', { duration: formatHm(recordedSeconds), title: todoTitle || '' })
            : t('timer.correctDurationMessage', { duration: formatHm(recordedSeconds) })}
        </p>
        <div className="duration-modal__fields">
          <label htmlFor={hoursId} className="duration-modal__field">
            <span>{t('timer.hours')}</span>
            <input
              id={hoursId}
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_HOURS}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </label>
          <label htmlFor={minutesId} className="duration-modal__field">
            <span>{t('timer.minutes')}</span>
            <input
              id={minutesId}
              type="number"
              inputMode="numeric"
              min={0}
              max={59}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </label>
        </div>
        {!isValid && <p className="duration-modal__error" role="alert">{t('timer.invalidDuration')}</p>}
      </form>
    </Modal>
  );
};

export default DurationCorrectionModal;
